import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
    RadioGroup, FormControlLabel, Radio, Typography, Box, Skeleton, Stack, Avatar,
    Autocomplete, Checkbox, FormGroup, Divider
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { SaveButton } from "../../../../app/components/SaveButton";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { RequiredError } from "../../../../app/components/RequiredError";
import { CancelButton } from "../../../../app/components/CancelButton";
import { selectStructureNodeById } from "../../../device/structureSlice";
import { DocumentLanguages, DocumentTypes } from "../../../documents/documentTypes";
import { DocumentLanguageSelect } from "../../../documents/controls/DocumentLanguageSelect";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { alpha } from "@mui/material/styles";
import {
    CreationMode, DocumentToCreate, prepareValidationStage,
    PrepareValidationStagePayload, ProcessStageOverview
} from "../validationProcessApi";
import { ClientDocumentTemplateSelectField } from "../../../documents/templates/ClientDocumentTemplateSelectField";
import { LoadUsers } from "../../../../app/AppDataLoader";
import PersonIcon from "@mui/icons-material/Person";
import { selectAllUsers } from "../../../users/usersSlice";
import React from "react";

type ValidationDocType = Extract<DocumentTypes, "IQ" | "OQ" | "PQ">;

type UserOption = {
    userName: string;
    firstname: string;
    surname: string;
    email: string;
    position?: string;
};

export interface ValidationStageActivationProps {
    stageCode: "VALIDATION";                 // <== etap walidacji
    processId: AppId | null;
    structureId: number;
    processName?: string;
    processNumber?: string;
    deviceId?: string;
    onSuccess: (stage: ProcessStageOverview) => void;
    onCancel: () => void;
}

type PerDocState = {
    number: string;
    numberDirty: boolean;
    title: string;
    titleDirty: boolean;
    creationMode: CreationMode;
    templateId: string | "";
};

const DEFAULT_DOCS: ValidationDocType[] = ["IQ", "OQ", "PQ"];

export const ValidationStageActivation: React.FC<ValidationStageActivationProps> = (props) => {
    const dispatch = useAppDispatch();
    const allUsers = useAppSelector(selectAllUsers);

    const users: UserOption[] = React.useMemo(
        () =>
            (allUsers ?? []).map((u: any) => ({
                userName: String(u.userName),
                firstname: u.firstName ?? u.firstname ?? "",
                surname: u.lastName ?? u.surname ?? "",
                email: u.email ?? "",
                position: u.position,
            })),
        [allUsers]
    );

    const findUserByUsername = (username: string | null, list: UserOption[]) =>
        username ? list.find(u => u.userName === username) ?? null : null;

    // Wspólne pola
    const [language, setLanguage] = useState<DocumentLanguages>("EN");
    const [responsible, setResponsible] = useState<string | null>(null);
    const [responsibleDirty, setResponsibleDirty] = useState(false);

    // Jakie dokumenty tworzymy
    const [selectedTypes, setSelectedTypes] = useState<ValidationDocType[]>(["IQ", "OQ", "PQ"]);

    // Per-dokument stan
    const [docState, setDocState] = useState<Record<ValidationDocType, PerDocState>>({
        IQ: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
        OQ: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
        PQ: { number: "", numberDirty: false, title: "", titleDirty: false, creationMode: "TEMPLATE", templateId: "" },
    });

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const structure = useAppSelector((state) =>
        props.structureId ? selectStructureNodeById(state, props.structureId) : null
    );

    const structureDisplay = useMemo(() => {
        const n = structure?.name ?? "";
        const nEN = structure?.name_EN ?? "";
        const nm = language === "EN" ? (nEN || n) : (n || nEN);
        const code = structure?.code ? ` (${structure.code})` : "";
        return (nm || structure?.code) ? `${nm}${code}` : "-";
    }, [structure?.name, structure?.name_EN, structure?.code, language]);

    // Generatory numeru i tytułu per typ
    const buildDefaultNumber = (type: string, structCode?: string, lang?: string, date: Date = new Date()) => {
        if (!type || !lang) return "";
        const year = date.getFullYear();
        const t = String(type).toUpperCase();
        const sc = (structCode ? String(structCode) : "GEN").toUpperCase().replace(/\s+/g, "-");
        const l = String(lang).toUpperCase();
        return `${t}-${sc}-${year}-${l}`;
    };

    const buildDefaultTitle = (type: string, langCode?: string, st?: { name?: string; name_EN?: string; code?: string }) => {
        if (!type || !langCode) return "";
        const lc = langCode.toUpperCase();
        const phrase = lc === "EN" ? "Protocol for" : "Protokół dla";
        const deviceName = lc === "EN" ? (st?.name_EN ?? st?.name ?? "") : (st?.name ?? st?.name_EN ?? "");
        const devicePart = deviceName ? `"${deviceName}"${st?.code ? ` (${st.code})` : ""}` : "";
        return devicePart ? `${type} — ${phrase} ${devicePart}` : `${type} — ${phrase}`;
    };

    // Ustaw domyślne numery/tytuły przy starcie i przy zmianach języka/struktury
    useEffect(() => {
        setDocState(prev => {
            const copy = { ...prev };
            DEFAULT_DOCS.forEach(dt => {
                const cur = copy[dt];
                if (!cur.numberDirty) {
                    cur.number = buildDefaultNumber(dt, structure?.code, String(language));
                }
                if (!cur.titleDirty) {
                    cur.title = buildDefaultTitle(dt, String(language), structure ?? undefined);
                }
            });
            return { ...copy };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, structure?.name, structure?.name_EN, structure?.code]);

    // Walidacja
    const isResponsibleOk = !!(responsible && responsible.trim() !== "");
    const isPerDocOk = selectedTypes.every(dt => {
        const s = docState[dt];
        const numberOk = !!s.number.trim();
        const titleOk = !!s.title.trim();
        const templateOk = s.creationMode === "EMPTY" ? true : (s.templateId !== "" && Number(s.templateId) > 0);
        return numberOk && titleOk && templateOk;
    });
    const isFormValid = !!props.processId && !!language && isResponsibleOk && isPerDocOk && selectedTypes.length > 0;

    // Handlery per-dokument
    const handleDocNumberChange = (dt: ValidationDocType, v: string) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], number: v, numberDirty: true } }));
    };
    const handleDocTitleChange = (dt: ValidationDocType, v: string) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], title: v, titleDirty: true } }));
    };
    const handleDocModeChange = (dt: ValidationDocType, mode: CreationMode) => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], creationMode: mode } }));
    };
    const handleDocTemplateChange = (dt: ValidationDocType, id: string | "") => {
        setDocState(prev => ({ ...prev, [dt]: { ...prev[dt], templateId: id } }));
    };

    // Zaznaczanie IQ/OQ/PQ
    const toggleType = (dt: ValidationDocType) => {
        setSelectedTypes(prev =>
            prev.includes(dt) ? prev.filter(x => x !== dt) : [...prev, dt]
        );
    };

    const handleSaveClick = async () => {
        if (!isFormValid) {
            setServerError("fill-all-required-fields");
            return;
        }

        const documents: DocumentToCreate[] = selectedTypes.map((dt) => {
            const st = docState[dt];
            return {
                type: dt as unknown as DocumentTypes, // w Twoim typie DocumentTypes powinny być "IQ" | "OQ" | "PQ"
                number: st.number.trim(),
                title: st.title.trim(),
                language,
                responsible: responsible?.trim() || null,
                creationMode: st.creationMode,
                templateId: st.creationMode === "TEMPLATE" ? Number(st.templateId) : 0,
            };
        });

        const payload: PrepareValidationStagePayload = {
            stageCode: props.stageCode,
            processId: props.processId!,
            documents,
            toLink: []
        };

        setSaving(true);
        setServerError("");
        try {
            const result = await dispatch(prepareValidationStage(payload)).unwrap();
            dispatch(showSuccess("saved"));
            props.onSuccess(result as ProcessStageOverview);
        } catch (e) {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md"
            slotProps={{
                backdrop: { sx: { bgcolor: (t) => alpha(t.palette.grey[900], 0.5) } },
            }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <Localized id="val-stage-activation-title">
                    <span>Przygotowanie dokumentów walidacyjnych (IQ / OQ / PQ)</span>
                </Localized>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {/* Info */}
                <Box sx={(t) => ({
                    display: "flex", alignItems: "flex-start", gap: 2, p: 2, mb: 2,
                    border: `1px solid ${t.palette.divider}`, borderRadius: 1.5, bgcolor: t.palette.action.hover,
                })}>
                    <InfoOutlinedIcon sx={{ mt: 1, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Dokumenty zostaną przygotowane dla:
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 0.5, columnGap: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <AssignmentOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Proces:</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {(props.processName || "-")}{props.processNumber ? ` (${props.processNumber})` : ""}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <DevicesOtherOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Urządzenie:</Box>
                            </Typography>
                            <Typography variant="body2">{props.deviceId || "-"}</Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <ApartmentOutlinedIcon fontSize="small" />
                                <Box component="span" sx={{ fontWeight: 600 }}>Struktura:</Box>
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                {structureDisplay}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Wspólne: Język + Odpowiedzialny */}
                <Grid container spacing={2} sx={{ mt: 1, mb: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <DocumentLanguageSelect defaultValue={language} required onChange={(val: any) => setLanguage(val)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <LoadUsers component={<Skeleton variant="rectangular" height={56} />}>
                            <Autocomplete<UserOption, false, false, false>
                                options={users}
                                onChange={(_, u) => { setResponsible(u?.userName ?? null); setResponsibleDirty(true); }}
                                onBlur={() => setResponsibleDirty(true)}
                                value={findUserByUsername(responsible, users)}
                                isOptionEqualToValue={(o, v) => o.userName === v.userName}
                                getOptionLabel={(u) => (u ? `${u.firstname} ${u.surname} (${u.email})` : "")}
                                renderOption={(props, u) => (
                                    <li {...props} key={u.userName}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12, lineHeight: 1 }} src={(u as any).avatarUrl}>
                                                {<PersonIcon sx={{ fontSize: 16 }} />}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>{u.firstname} {u.surname}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {u.email}{u.position ? ` · ${u.position}` : ""}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Osoba odpowiedzialna"
                                        placeholder="Wybierz osobę"
                                        required
                                        error={responsibleDirty && !isResponsibleOk}
                                        helperText={responsibleDirty && !isResponsibleOk ? <RequiredError /> : null}
                                    />
                                )}
                            />
                        </LoadUsers>
                    </Grid>
                </Grid>

                {/* Wybór typów dokumentów */}
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Jakie dokumenty utworzyć?</Typography>
                    <FormGroup row>
                        {(["IQ", "OQ", "PQ"] as ValidationDocType[]).map(dt => (
                            <FormControlLabel
                                key={dt}
                                control={<Checkbox checked={selectedTypes.includes(dt)} onChange={() => toggleType(dt)} />}
                                label={dt}
                            />
                        ))}
                    </FormGroup>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Sekcje per dokument */}
                <Stack spacing={2}>
                    {selectedTypes.map((dt) => {
                        const s = docState[dt];
                        return (
                            <Box key={dt} sx={(t) => ({
                                border: `1px solid ${t.palette.divider}`, borderRadius: 1, p: 1.25,
                                bgcolor: t.palette.action.hover,
                            })}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{dt}</Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            required fullWidth InputLabelProps={{ shrink: true }}
                                            value={s.number} error={!s.number.trim()}
                                            onChange={(e) => handleDocNumberChange(dt, e.target.value)}
                                            label={<Localized id={`document-number-${dt}`}><span>_Numer dokumentu ({dt})</span></Localized>}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            required fullWidth InputLabelProps={{ shrink: true }}
                                            value={s.title} error={!s.title.trim()}
                                            onChange={(e) => handleDocTitleChange(dt, e.target.value)}
                                            label={<Localized id={`document-title-${dt}`}><span>_Tytuł ({dt})</span></Localized>}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mt: 0.5, mb: 0.5 }}>Tryb tworzenia</Typography>
                                        <RadioGroup
                                            row value={s.creationMode}
                                            onChange={(e) => handleDocModeChange(dt, e.target.value as CreationMode)}
                                        >
                                            <FormControlLabel value="EMPTY" control={<Radio size="small" />} sx={{ mr: 2 }} label={<Typography variant="body2">Pusty</Typography>} />
                                            <FormControlLabel value="TEMPLATE" control={<Radio size="small" />} label={<Typography variant="body2">Z szablonu</Typography>} />
                                        </RadioGroup>

                                        {s.creationMode === "TEMPLATE" && (
                                            <Box sx={{ mt: 1 }}>
                                                <ClientDocumentTemplateSelectField
                                                    structureId={props.structureId}
                                                    documentType={dt as unknown as DocumentTypes}
                                                    value={s.templateId}
                                                    onChange={(id) => handleDocTemplateChange(dt, id)}
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        );
                    })}
                </Stack>

                {serverError && (
                    <Alert sx={{ mt: 2 }} onClose={() => setServerError("")} severity="error">
                        <Localized id={serverError}><span>Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <SaveButton loading={saving} onClick={handleSaveClick} />
                <CancelButton loading={saving} onClick={props.onCancel} />
            </DialogActions>
        </Dialog>
    );
};
